import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, User, Calendar, Mail, Phone, FileText, Clock } from "lucide-react";
import { GlobalAnalysisModern } from "@/components/patient/GlobalAnalysisModern";
import { PacienteDadosBasicos } from "@/components/admin/PacienteDadosBasicos";
import { PatientHealthRecords } from "@/components/patient/PatientHealthRecords";
import PatientResultados from "@/components/patient/PatientResultados";
import { DocumentAnalysisModal } from "@/components/patient/DocumentAnalysisModal";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCPF, formatPhone } from "@/lib/formatters";
import { motion } from "framer-motion";
import { TabErrorBoundary } from "@/components/admin/TabErrorBoundary";
import { toast } from "sonner";
import { usePatientData } from "@/hooks/usePatientData";

const PacienteDetails = () => {
  console.log('🚀 PacienteDetails component mounted');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("dados");

  // Modal states
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<'smartfile' | 'document'>('smartfile');

  const {
    labResults,
    subcategories,
    registrosSaude,
    loading: dataLoading,
    refetch
  } = usePatientData(id);

  console.log('📍 Patient ID from params:', id);

  useEffect(() => {
    console.log('🔄 useEffect triggered, id:', id);
    if (id) {
      loadPatientData();
    } else {
      console.error('❌ No patient ID provided in URL');
      setLoading(false);
    }
  }, [id]);

  // Atualizar título da aba com nome do paciente
  useEffect(() => {
    if (patient?.full_name) {
      document.title = `elinia - ${patient.full_name}`;
    }
    return () => {
      document.title = "elinia - Saúde com Inteligência";
    };
  }, [patient?.full_name]);

  const loadPatientData = async () => {
    try {
      console.log('🔍 Carregando dados do paciente:', id);

      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error('❌ Erro Supabase:', error);
        throw error;
      }

      console.log('✅ Paciente carregado com sucesso:', data);
      setPatient(data);
    } catch (error) {
      console.error("❌ Erro ao carregar paciente:", error);
      toast.error("Erro ao carregar dados do paciente");
      navigate("/admin/cadastros/pacientes");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    console.log('⏳ Showing loading state');
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card className="p-6">
          <Skeleton className="h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (!patient) {
    console.log('❌ No patient data, showing not found');
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Paciente não encontrado</p>
        <Button onClick={() => navigate("/admin/cadastros/pacientes")} className="mt-4">
          Voltar para listagem
        </Button>
      </div>
    );
  }

  console.log('✅ Rendering patient details for:', patient.full_name);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const handleViewDocument = (docId: string, docType: 'smartfile' | 'document') => {
    console.log('📄 Opening document:', docId, docType);
    setSelectedDocumentId(docId);
    setSelectedDocumentType(docType);
    setShowAnalysisModal(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/cadastros/pacientes")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>

      <Card className="p-6">
        <div className="flex items-start gap-6">
          <Avatar className="h-20 w-20">
            <AvatarImage src={patient.avatar_url} />
            <AvatarFallback className="text-xl">
              {getInitials(patient.full_name)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 space-y-3">
            <div>
              <h1 className="text-3xl font-bold">{patient.full_name || "Sem nome"}</h1>
              <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                {patient.cpf ? (
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {formatCPF(patient.cpf)}
                  </span>
                ) : null}
                {patient.data_nascimento ? (
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {new Date(patient.data_nascimento).toLocaleDateString("pt-BR")}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              {patient.email ? (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  {patient.email}
                </span>
              ) : null}
              {patient.celular ? (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  {formatPhone(patient.celular)}
                </span>
              ) : null}
              {patient.sexo ? (
                <Badge variant="outline">{patient.sexo}</Badge>
              ) : null}
              <Badge variant={patient.status === "active" ? "default" : "secondary"}>
                {patient.status === "active" ? "Ativo" : "Inativo"}
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <Tabs value={activeTab} onValueChange={(v) => {
          console.log('📑 Tab changed to:', v);
          setActiveTab(v);
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dados">
              <User className="h-4 w-4 mr-2" />
              Dados
            </TabsTrigger>
            <TabsTrigger value="registros">
              <FileText className="h-4 w-4 mr-2" />
              Registros
            </TabsTrigger>
            <TabsTrigger value="analises">
              <FileText className="h-4 w-4 mr-2" />
              Análises IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dados" className="mt-6">
            <TabErrorBoundary tabName="Dados">
              {activeTab === "dados" && <PacienteDadosBasicos patientId={id || ""} />}
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="analises" className="mt-6">
            <TabErrorBoundary tabName="Análises">
              {activeTab === "analises" && <GlobalAnalysisModern patientId={id || ""} />}
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="registros" className="mt-6">
            <TabErrorBoundary tabName="Registros">
              {activeTab === "registros" && (
                <PatientHealthRecords
                  patientId={id || ""}
                  records={registrosSaude}
                  onRefresh={refetch}
                />
              )}
            </TabErrorBoundary>
          </TabsContent>
        </Tabs>
      </Card>

      {showAnalysisModal && selectedDocumentId && (
        <DocumentAnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => {
            setShowAnalysisModal(false);
            setSelectedDocumentId(null);
          }}
          documentId={selectedDocumentId}
          documentType={selectedDocumentType}
          patientId={id || ""}
        />
      )}
    </motion.div>
  );
};

export default PacienteDetails;
